/**
 * References as cards, never ids (section 4): a card with the item's picture, name, kind and
 * status badge, and a searchable picker to choose items with. Used for a collection's items, a
 * series' parts, credits, a session's stage and a row's collections.
 */

import { useMemo, useState } from "react";
import Button from "@web-ui/components/Button";
import Empty from "@web-ui/components/Empty";
import Field from "@web-ui/components/Field";
import FilterButtons from "@web-ui/components/FilterButtons";
import Modal from "@web-ui/components/Modal";
import type { Entity, EntityType } from "@/api/admin";
import { StatusBadge } from "@/components/status-badge";
import { href, itemRoute } from "@/lib/route";
import { useStudio } from "@/shell/studio";
import { lower, noun } from "@/vocabulary/vocabulary";

type Img = { url?: string; sizes?: Array<{ url: string; width: number }> };

/** A small picture of an item: its poster, thumbnail or backdrop; a person's photo. */
export function thumbOf(e: Entity | undefined): string | undefined {
  if (!e) return undefined;
  const art = (e.data["artwork"] ?? {}) as Record<string, Img>;
  const img = (e.type === "person" ? (e.data["image"] as Img | undefined) : undefined) ?? art["thumbnail"] ?? art["backdrop"] ?? art["poster"] ?? art["square"] ?? art["hero"];
  return img?.sizes?.[0]?.url ?? img?.url;
}

export function PickerCard({ entity, missingId, index, actions, link = true }: { entity: Entity | undefined; missingId?: string; index?: string | number; actions?: React.ReactNode; link?: boolean }) {
  const { client, pack, stateOf } = useStudio();
  const thumb = thumbOf(entity);
  const name = entity ? entity.label || "(no name)" : "Not in your Library any more";
  return (
    <div className="flex min-h-16 items-center gap-3 rounded-2xl bg-b-surface1 p-2 pe-3">
      {index !== undefined ? <span className="w-12 shrink-0 text-center text-caption text-t-secondary tabular-nums">{index}</span> : null}
      {thumb ? <img src={thumb} alt="" className="h-12 w-12 shrink-0 rounded-xl object-cover" loading="lazy" /> : <span className="h-12 w-12 shrink-0 rounded-xl bg-b-surface3" aria-hidden="true" />}
      <div className="flex min-w-0 grow flex-col gap-1">
        {entity && link ? <a className="truncate text-body-2 font-medium hover:underline focus-visible:underline" href={href(itemRoute(client.brand.id, entity.type, entity.id))}>{name}</a> : <span className={`truncate text-body-2 ${entity ? "font-medium" : "text-t-secondary"}`}>{name}</span>}
        <div className="flex flex-wrap items-center gap-2">
          {entity ? <StatusBadge state={stateOf(entity.type, entity.id) ?? entity.publishState} availability={entity.data["availability"] as { from?: string; until?: string } | undefined} /> : null}
          <span className="text-caption text-t-secondary">{entity ? noun(pack, entity.type) : `It was removed (${missingId}). Remove it here too.`}</span>
        </div>
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-1">{actions}</div> : null}
    </div>
  );
}

/** Up, down and remove for a row of an ordered list. */
export function RowActions({ name, index, count, onMove, onRemove }: { name: string; index: number; count: number; onMove: (to: number) => void; onRemove: () => void }) {
  return (
    <>
      <Button type="button" size="xs" variant="ghost" icon="arrow-up" isIconOnly aria-label={`Move ${name} up`} disabled={index === 0} onClick={() => onMove(index - 1)} />
      <Button type="button" size="xs" variant="ghost" icon="arrow-down" isIconOnly aria-label={`Move ${name} down`} disabled={index === count - 1} onClick={() => onMove(index + 1)} />
      <Button type="button" size="xs" variant="ghost" icon="close" isIconOnly aria-label={`Remove ${name}`} onClick={onRemove} />
    </>
  );
}

export function ItemPicker({ open, onClose, types, catalogue, exclude = [], multiple = true, title, onPick }: {
  open: boolean;
  onClose: () => void;
  types: EntityType[];
  catalogue: Entity[] | null;
  exclude?: string[];
  multiple?: boolean;
  title: string;
  onPick: (ids: string[]) => void;
}) {
  const { pack } = useStudio();
  const [q, setQ] = useState("");
  const [type, setType] = useState<EntityType | "all">("all");
  const [chosen, setChosen] = useState<string[]>([]);
  const shown = useMemo(() => (catalogue ?? [])
    .filter((e) => types.includes(e.type) && (type === "all" || e.type === type) && e.status !== "archived" && !exclude.includes(e.id))
    .filter((e) => !q.trim() || e.label.toLowerCase().includes(q.trim().toLowerCase()))
    .sort((a, b) => b.updatedAt - a.updatedAt), [catalogue, types, type, exclude, q]);
  const close = () => { setChosen([]); setQ(""); onClose(); };
  const pick = (id: string) => {
    if (!multiple) { onPick([id]); close(); return; }
    setChosen((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
  };
  const many = types.length === 1 ? lower(noun(pack, types[0]!, "many")) : "items";
  return (
    <Modal open={open} onClose={close} size="lg">
      <div className="flex flex-col gap-4">
        <h2 className="text-h5">{title}</h2>
        <Field aria-label={`Search ${many}`} size="sm" placeholder={`Search ${many} by name`} value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
        {types.length > 1 ? (
          <FilterButtons label="Show" value={type} onChange={(v) => setType(v)} items={[{ id: "all" as const, label: "All" }, ...types.map((t) => ({ id: t, label: noun(pack, t, "many") }))]} />
        ) : null}
        {catalogue === null ? <p className="text-body-2 text-t-secondary">Loading your Library…</p> : shown.length === 0 ? (
          <Empty icon="search" title={q ? "Nothing matches" : `No ${many} to add`} description={q ? "Try another name." : `Everything is already here, or there are no ${many} yet.`} />
        ) : (
          <ul className="flex max-h-[55vh] flex-col gap-2 overflow-auto" aria-label="Results">
            {shown.map((e) => {
              const on = chosen.includes(e.id);
              return (
                <li key={`${e.type}:${e.id}`}>
                  <button type="button" aria-pressed={multiple ? on : undefined} onClick={() => pick(e.id)}
                    className={`block w-full rounded-2xl text-start outline-offset-2 focus-visible:outline-2 focus-visible:outline-s-focus ${on ? "ring-2 ring-accent" : ""}`}>
                    <PickerCard entity={e} link={false} actions={multiple ? <span className={`flex size-6 items-center justify-center rounded-full border-2 ${on ? "border-accent bg-accent text-on-accent" : "border-s-stroke2"}`} aria-hidden="true">{on ? "✓" : ""}</span> : undefined} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {multiple ? (
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="primary" disabled={!chosen.length} onClick={() => { onPick(chosen); close(); }}>{chosen.length ? `Add ${chosen.length}` : "Add"}</Button>
            <Button type="button" variant="outline" onClick={close}>Cancel</Button>
          </div>
        ) : <div><Button type="button" variant="outline" onClick={close}>Cancel</Button></div>}
      </div>
    </Modal>
  );
}
